import { formatDateOnly, formatTimestampDateTime } from './dates';
import { formatServiceRequestActivityLine, type ServiceRequestActivityContext } from './service-requests';
import {
  memberDisplayName,
  workItemSubtypeLabel,
  type LabelDto,
  type MemberDto,
  type WorkItemSubtypeDefinition,
} from './work-types';

export type ActivityFormatOptions = {
  /** Present when the timeline belongs to a Service Request (contract §6/§12). */
  serviceRequest?: ServiceRequestActivityContext;
  subtypes?: WorkItemSubtypeDefinition[];
};

export type ActivityDto = {
  id: string;
  verb: string;
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  actor_id: string | null;
  actor_kind: string;
  created_at: string;
};

export type ActivityEvent = {
  id: string;
  actor_id: string | null;
  actor_kind: string;
  created_at: string;
  rows: ActivityDto[];
};

const MERGE_WINDOW_MS = 3000;
const PROPERTY_FIELDS = new Set([
  'title',
  'description',
  'state',
  'priority',
  'assignees',
  'labels',
  'parent',
  'release',
  'initiative',
  'start_date',
  'due_date',
  'estimate_hours',
  'archived_at',
  'subtype',
  'blocked',
  'blocked_by',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isActivityDto(value: unknown): value is ActivityDto {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.verb === 'string' &&
    (value.field === null || typeof value.field === 'string') &&
    (value.old_value === null || typeof value.old_value === 'string') &&
    (value.new_value === null || typeof value.new_value === 'string') &&
    (value.actor_id === null || typeof value.actor_id === 'string') &&
    typeof value.actor_kind === 'string' &&
    typeof value.created_at === 'string'
  );
}

export function actorLabel(
  actorId: string | null,
  actorKind: string,
  members: MemberDto[],
): string {
  if (actorId) {
    const member = members.find((row) => row.user_id === actorId);
    if (member) return memberDisplayName(member);
  }
  if (actorKind === 'AGENT') return 'Agent';
  if (actorKind === 'SYSTEM') return 'Pulse';
  return 'Someone';
}

function parseIdList(raw: string | null): string[] | null {
  if (raw == null || raw === '') return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === 'string')) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function namesForIds(ids: string[], lookup: Map<string, string>): string {
  if (ids.length === 0) return 'nobody';
  return ids.map((id) => lookup.get(id) ?? 'unknown').join(', ');
}

function displayValue(
  field: string,
  raw: string | null,
  labels: LabelDto[],
  members: MemberDto[],
  subtypes?: WorkItemSubtypeDefinition[],
): string {
  if (raw == null || raw === '') return 'none';
  if (field === 'start_date' || field === 'due_date') {
    return formatDateOnly(raw) ?? raw;
  }
  if (field === 'estimate_hours') {
    return `${raw}h`;
  }
  if (field === 'subtype') {
    return workItemSubtypeLabel(raw, subtypes);
  }
  if (field === 'assignees') {
    const ids = parseIdList(raw);
    if (!ids) return 'updated';
    return namesForIds(ids, new Map(members.map((row) => [row.user_id, memberDisplayName(row)])));
  }
  if (field === 'blocked_by') {
    const ids = raw.split(',').filter(Boolean);
    return namesForIds(ids, new Map(members.map((row) => [row.user_id, memberDisplayName(row)])));
  }
  if (field === 'labels') {
    const ids = parseIdList(raw);
    if (!ids) return 'updated';
    return namesForIds(ids, new Map(labels.map((row) => [row.id, row.name])));
  }
  return raw;
}

function fieldTitle(field: string): string {
  if (field === 'start_date') return 'start date';
  if (field === 'due_date') return 'due date';
  if (field === 'estimate_hours') return 'estimate';
  if (field === 'archived_at') return 'archive state';
  if (field === 'subtype') return 'task type';
  return field.replaceAll('_', ' ');
}

export function formatActivityLine(
  row: ActivityDto,
  members: MemberDto[],
  labels: LabelDto[],
  options: ActivityFormatOptions = {},
): string {
  const serviceRequestLine = formatServiceRequestActivityLine(row, options.serviceRequest);
  if (serviceRequestLine) return serviceRequestLine;
  if (row.verb === 'created' && !row.field) return 'Created this item';
  if (row.field === 'comment') {
    if (row.verb === 'created') return 'Added a comment';
    if (row.verb === 'updated') return 'Edited a comment';
    if (row.verb === 'deleted') return 'Deleted a comment';
  }
  if (row.field === 'attachment') {
    if (row.verb === 'created') return 'Added an attachment';
    if (row.verb === 'deleted') return 'Removed an attachment';
  }
  if (row.field === 'relation') {
    if (row.verb === 'linked') return 'Linked a work item';
    if (row.verb === 'unlinked') return 'Removed a link';
  }
  if (row.field === 'description') return 'Updated the description';
  if (row.field === 'live') return row.new_value ? 'Marked live' : 'Took off live';
  if (row.field === 'blocked') {
    if (!row.new_value) return 'Unblocked';
    if (!row.old_value) return `Marked blocked: ${row.new_value}`;
    return `Changed the blocked reason to: ${row.new_value}`;
  }
  if (row.field && row.verb === 'updated') {
    const next = displayValue(row.field, row.new_value, labels, members, options.subtypes);
    const prev = displayValue(row.field, row.old_value, labels, members, options.subtypes);
    if (row.new_value == null || row.new_value === '') {
      return `Cleared ${fieldTitle(row.field)}`;
    }
    if (row.old_value == null || row.old_value === '') {
      return `Set ${fieldTitle(row.field)} to ${next}`;
    }
    if (prev === next) return `Updated ${fieldTitle(row.field)}`;
    return `Changed ${fieldTitle(row.field)} from ${prev} to ${next}`;
  }
  if (row.field) return `${row.verb} ${fieldTitle(row.field)}`;
  return row.verb;
}

function createdMs(row: ActivityDto): number {
  const parsed = new Date(row.created_at).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Detail patches write one row per changed SR field (contract §6); they merge like core properties. */
function isMergeableField(field: string | null): boolean {
  if (!field) return false;
  return PROPERTY_FIELDS.has(field) || field.startsWith('service_request.');
}

function canMerge(group: ActivityEvent, row: ActivityDto): boolean {
  const last = group.rows[group.rows.length - 1];
  if (!last) return false;
  if (group.actor_id !== row.actor_id || group.actor_kind !== row.actor_kind) return false;
  if (row.verb !== 'updated' || last.verb !== 'updated') return false;
  if (!isMergeableField(row.field) || !isMergeableField(last.field)) return false;
  return Math.abs(createdMs(row) - createdMs(last)) <= MERGE_WINDOW_MS;
}

/** Audit-only rows kept on the server for stale-approval checks; meaningless to readers. */
const HIDDEN_FIELDS = new Set(['service_request.request_version']);

/** Oldest rows first in each group; groups newest-first for the feed. */
export function groupActivity(rows: ActivityDto[]): ActivityEvent[] {
  const chronological = rows.filter((row) => !row.field || !HIDDEN_FIELDS.has(row.field)).sort((a, b) => createdMs(a) - createdMs(b) || a.id.localeCompare(b.id));
  const groups: ActivityEvent[] = [];
  for (const row of chronological) {
    const current = groups[groups.length - 1];
    if (current && canMerge(current, row)) {
      current.rows.push(row);
      continue;
    }
    groups.push({
      id: row.id,
      actor_id: row.actor_id,
      actor_kind: row.actor_kind,
      created_at: row.created_at,
      rows: [row],
    });
  }
  return groups.reverse();
}

export function formatActivityWhen(iso: string): string {
  return formatTimestampDateTime(iso) ?? iso;
}

export const ACTIVITY_PREVIEW_COUNT = 4;

/** Newest-first feed: keep the latest groups, hide the rest until expanded. */
export function previewActivityEvents(
  events: ActivityEvent[],
  expanded: boolean,
  previewCount = ACTIVITY_PREVIEW_COUNT,
): { visible: ActivityEvent[]; hiddenCount: number } {
  if (expanded || events.length <= previewCount) {
    return { visible: events, hiddenCount: 0 };
  }
  return {
    visible: events.slice(0, previewCount),
    hiddenCount: events.length - previewCount,
  };
}
